import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BrainCircuit,
  Coins,
  FileText,
  Image,
  LockKeyhole,
  MonitorDown,
  PackageCheck,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PageHeader } from "@packages/ui";
import { HomeFooter } from "../home/HomeFooter";
import { HomeHeader } from "../home/HomeHeader";

interface LegalSection {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly body: readonly string[];
}

const legalSections: readonly LegalSection[] = [
  {
    icon: FileText,
    title: "一、协议适用范围",
    body: [
      "本协议适用于 Harness Station 网站、账号系统、管理后台面向用户开放的服务，以及配套的 DSH Desktop 客户端功能。你注册、登录、下载、更新或使用相关能力，即表示你已阅读并同意本协议。",
      "Harness Station 主要为 DSH Desktop 提供账号登录、系统模型、联网检索、图片生成、OCR、Skills 市场、积分计费和桌面版本更新等云端能力。DSH Desktop 的本地工作区、对话产物和用户主动保存的文件，默认保留在你的电脑上。",
    ],
  },
  {
    icon: BadgeCheck,
    title: "二、账号注册与登录",
    body: [
      "你可以使用平台支持的邮箱、手机号、密码、短信验证码和图形验证码完成注册或登录。验证码用于降低暴力破解、短信滥用和自动化攻击风险，请勿转发、出租或出售你的账号与验证码。",
      "你应保证注册信息真实、准确、可用，并及时更新。因账号共享、密码泄露、在不可信设备登录或未及时保护账号造成的损失，由你自行承担；如发现异常登录或非本人操作，请尽快通过平台支持入口联系我们。",
    ],
  },
  {
    icon: BrainCircuit,
    title: "三、模型与智能能力",
    body: [
      "系统模型由平台统一接入和维护，你无需在本地配置各模型供应商的 API Key。模型名称、说明、是否支持图片、思考、工具调用、参考图数量和积分单价等信息，以客户端或网站展示为准。",
      "当你在 DSH Desktop 中选择系统模型、联网检索、图片生成、OCR 或其他云端能力时，相关请求内容、必要上下文、文件内容或图片会被发送至 Harness Station，并可能由平台配置的第三方模型、检索、图片、OCR 或基础设施服务处理。",
    ],
  },
  {
    icon: Search,
    title: "四、联网检索与外部内容",
    body: [
      "联网检索能力用于帮助你获取公开网络信息或平台配置的数据来源结果。检索结果可能受第三方网页、搜索服务、网络连接、版权限制和时效性影响，平台不保证外部内容完整、准确或持续可访问。",
      "你应自行判断外部资料是否适合用于你的任务。对需要专业判断的医学、法律、金融、科研或工程场景，请以原始资料、权威数据库、专业人员意见和实际验证结果为准。",
    ],
  },
  {
    icon: Image,
    title: "五、图片生成、图片编辑与 OCR",
    body: [
      "图片生成、图片编辑和 OCR 能力会根据你的文本提示、参考图、上传图片或文件内容生成结果。你应确认输入内容拥有合法使用权，不得上传侵犯他人隐私、知识产权、商业秘密或违反法律法规的内容。",
      "生成图片、识别文本和结构化结果可能存在偏差、遗漏或错误。你负责在使用、发表、交付或商业化之前进行人工复核，并遵守第三方模型供应商和平台公布的内容安全要求。",
    ],
  },
  {
    icon: PackageCheck,
    title: "六、Skills 市场与客户端安装",
    body: [
      "Skills 市场用于发布和安装可扩展能力包。管理员发布的 Skill 会展示名称、标识、说明、分类、标签和下载地址等信息；客户端安装时会下载并解压到本机 DSH Skills 目录。",
      "如果本机已存在同名 Skill，客户端会提示你确认是否覆盖。安装或更新 Skill 前，请确认其来源、用途和适用范围；Skill 对本地文件、网络服务或外部命令的使用，仍需遵守 DSH Desktop 的权限设置与安全策略。",
    ],
  },
  {
    icon: Coins,
    title: "七、积分、充值与扣费",
    body: [
      "积分用于调用系统模型、检索、图片、OCR 和其他云端能力。不同能力或模型的积分单价、套餐价格、到账数量和有效期，以购买页面、模型列表或实际调用前展示的信息为准。",
      "平台会记录充值、赠送、消耗、退款和过期等积分流水。因平台系统异常或上游服务异常导致已扣费但服务未完成的，平台可按规则退回积分；用户主动取消、关闭客户端、终止网络连接或在请求已提交后中断任务，通常不退回已消耗积分。",
    ],
  },
  {
    icon: MonitorDown,
    title: "八、客户端下载与更新",
    body: [
      "平台可能提供安装版、便携版以及不同系统架构的 DSH Desktop 下载包，并通过版本清单、文件地址和校验值支持客户端检查更新。你可以根据设备环境选择合适版本。",
      "更新过程可能需要下载文件、退出当前客户端并启动安装或替换程序。请在更新前保存重要工作；如自动安装失败，你可以根据页面或客户端提示手动打开已下载的安装文件。",
    ],
  },
  {
    icon: LockKeyhole,
    title: "九、隐私与数据处理",
    body: [
      "平台会处理账号资料、手机号、邮箱、验证码记录、登录设备信息、积分流水、订单信息、下载更新记录、服务调用日志和必要的故障诊断信息，用于提供服务、保障安全、核算费用、处理争议和满足法律合规要求。",
      "DSH Desktop 的本地工作区文件、对话产物和用户保存的结果默认保留在你的电脑上。只有当你主动使用云端模型、检索、图片、OCR、Skill 下载、更新检查等能力时，相关请求所需的数据才会发送至云端或第三方服务。",
      "平台不会在客户端或公开页面展示服务端密钥，也不会要求你为系统模型在本机配置第三方供应商密钥。除法律法规要求、争议处理、安全排查或服务履行所需外，平台不会主动向无关第三方披露你的账号资料和服务数据。",
    ],
  },
  {
    icon: ShieldCheck,
    title: "十、用户行为规范",
    body: [
      "你不得使用本服务从事违法违规、侵犯他人权益、传播恶意代码、攻击平台、绕过计费、批量注册、滥发短信验证码、抓取非授权数据或干扰第三方服务的行为。",
      "你应对自己输入、上传、生成、下载、分享和使用的内容负责。因你的违法使用、侵权内容、错误配置、超出授权范围使用 Skill 或将输出结果用于高风险决策造成的责任，由你自行承担。",
    ],
  },
  {
    icon: Sparkles,
    title: "十一、AI 输出与服务限制",
    body: [
      "AI、检索、OCR 和图片生成结果可能不准确、不完整、过时或不适合特定场景。平台不承诺输出内容一定满足你的专业、商业、法律、医学、科研或生产环境要求。",
      "受模型供应商、网络环境、并发负载、第三方服务、维护升级和不可抗力影响，服务可能出现延迟、失败、中断或结果变化。平台会尽力保障服务稳定，但不对不可控因素导致的损失承担超出法律规定的责任。",
    ],
  },
  {
    icon: FileText,
    title: "十二、协议变更与联系",
    body: [
      "平台可能根据产品功能、价格规则、第三方服务、法律法规或运营需要更新本协议。重大变更会通过网站、客户端或公告进行提示；变更生效后继续使用服务，视为你接受更新后的协议。",
      "如你对账号、积分、订单、隐私、数据处理或本协议有疑问，可以通过 Harness Station 网站提供的支持入口联系平台。",
    ],
  },
];

const TermsPage: React.FC = () => {
  const navigate = useNavigate();
  const goToDownload = () => navigate("/#download");

  return (
    <main className="home-landing min-h-screen overflow-x-hidden bg-bg-page text-text-primary">
      <HomeHeader />
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-10 sm:px-6 lg:py-12">
        <PageHeader
          eyebrow="Legal"
          title="用户协议与隐私声明"
          description="请在注册、登录或使用 Harness Station 与 DSH Desktop 前，了解账号、积分、云端能力、本地数据和隐私处理规则。"
        />

        <section className="space-y-5 rounded-[12px] border border-border-subtle bg-bg-surface p-6 text-[13.5px] leading-7 text-text-secondary shadow-sm">
          <div className="rounded-[10px] border border-border-subtle bg-bg-elevated px-4 py-3 text-[13px] text-text-secondary">
            本页面用于说明平台服务规则和基础隐私处理方式，不构成针对特定行业或场景的法律意见。若你代表组织使用本服务，请同时确认你已获得组织授权。
          </div>

          {legalSections.map((section) => (
            <LegalBlock key={section.title} icon={section.icon} title={section.title}>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </LegalBlock>
          ))}

          <p className="border-t border-border-subtle pt-5 text-[12px] text-text-muted">
            最近更新：2026-09-09。如需帮助，请通过 Harness Station 页面提供的支持入口联系平台。
          </p>
        </section>
      </div>
      <HomeFooter onDownload={goToDownload} />
    </main>
  );
};

const LegalBlock: React.FC<React.PropsWithChildren<{ title: string; icon: LucideIcon }>> = ({ title, icon: Icon, children }) => (
  <article className="space-y-2">
    <h2 className="flex items-center gap-2 text-[15px] font-semibold text-text-primary">
      <Icon className="h-4 w-4 text-primary-ink" />
      {title}
    </h2>
    <div className="space-y-2">{children}</div>
  </article>
);

export default TermsPage;
